import React, { useState, useCallback, memo, useMemo, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useSettings } from '../contexts/SettingsContext';
import { APP_NAME } from '../config/frontend';
import toast from 'react-hot-toast';

const Homepage = memo(() => {
  const [loginData, setLoginData] = useState({
    loginId: '',
    password: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [focusedField, setFocusedField] = useState(null);
  const { login, isAuthenticated, shouldNavigate } = useAuth();
  const { getCompanyName } = useSettings();
  const logoutShownRef = useRef(false);


  // Auto-login with remember me token on component mount
  useEffect(() => {
    const rememberMeToken = localStorage.getItem('remember_me_token');
    
    if (rememberMeToken && !isAuthenticated) {
      // Attempt auto-login
      const tryAutoLogin = async () => {
        setIsLoading(true);
        const authService = (await import('../services/authService.jsx')).default;
        const result = await authService.validateRememberMe(rememberMeToken);
        
        if (result.success) {
          // Auto-login successful, AuthContext will handle navigation
          console.log('Auto-login successful');
        } else {
          // Token invalid, user needs to login manually
        }
        setIsLoading(false);
      };
      
      tryAutoLogin();
    }
  }, [isAuthenticated]);


  // Memoize form state to prevent unnecessary re-renders
  const formState = useMemo(() => ({
    isDisabled: isLoading,
    buttonText: isLoading ? 'Signing In...' : 'Sign In',
    showSpinner: isLoading
  }), [isLoading]);

  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    setLoginData(prev => ({
      ...prev,
      [name]: value,
    }));
  }, []);

  const handleLogin = useCallback(async (e) => {
    e.preventDefault();
    if (formState.isDisabled) return;
    
    setIsLoading(true);

    try {
      const result = await login(loginData.loginId, loginData.password, rememberMe);
      
      if (result.success) {
        // Remember me token is handled by authService
        // No need to store passwords in localStorage anymore
        
        // Clear form on successful login
        setLoginData({
          loginId: '',
          password: '',
        });
        setIsLoading(false);
        // Navigation will happen automatically via AuthContext
        // Welcome message will be shown on Dashboard
      } else {
        // Show error toast for failed login
        toast.error(result.message || 'Invalid credentials Please try again.', {
          duration: 4000,
        });
        setIsLoading(false);
      }
    } catch (err) {
      console.error('Login error:', err);
      toast.error('Network error. Please check your connection.', {
        duration: 4000,
      });
      setIsLoading(false);
    }
  }, [login, loginData.loginId, loginData.password, formState.isDisabled, rememberMe]);

  return (
    <div className="min-h-screen relative overflow-hidden" style={{
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
    }}>
      {/* Background Image with Blend - Full Coverage */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-8"
        style={{
          backgroundImage: 'url(/nexapos.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          filter: 'blur(1px)',
        }}
      ></div>
      
      {/* Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-purple-900/85 via-indigo-900/75 to-purple-900/85"></div>

      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 rounded-full mix-blend-multiply filter blur-xl opacity-12 animate-blob" style={{backgroundColor: '#667eea'}}></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 rounded-full mix-blend-multiply filter blur-xl opacity-12 animate-blob animation-delay-2000" style={{backgroundColor: '#764ba2'}}></div>
        <div className="absolute top-40 left-40 w-80 h-80 rounded-full mix-blend-multiply filter blur-xl opacity-12 animate-blob animation-delay-4000" style={{backgroundColor: '#ffd700'}}></div>
      </div>

      {/* Main Content */}
      <div className="relative z-10 min-h-screen flex items-center justify-center p-4">
        <div className="w-full max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left Side - Brand and Features */}
            <div className="text-white space-y-12">
              {/* Brand Section */}
              <div className="space-y-6">
                <div className="flex items-center space-x-6">
                  <div className="relative">
                    <div className="w-20 h-20 rounded-2xl shadow-2xl flex items-center justify-center transform rotate-3 hover:rotate-0 transition-transform duration-300" style={{
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                    }}>
                      <img 
                        src="/nexapos-logo.png" 
                        alt="NexaPOS" 
                        className="w-12 h-12 rounded-lg" 
                        loading="lazy" 
                      />
                    </div>
                    <div className="absolute -inset-2 rounded-2xl blur opacity-30 animate-pulse" style={{
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                    }}></div>
                  </div>
                  <div>
                    <h1 className="text-5xl font-bold tracking-tight bg-gradient-to-r from-white to-yellow-200 bg-clip-text text-transparent">
                      {APP_NAME}
                    </h1>
                    <p className="text-xl font-light mt-2" style={{color: '#ffd700'}}>
                      Next Generation Point of Sale System
                    </p>
                  </div>
                </div>
                
                <p className="text-lg text-gray-300 leading-relaxed max-w-md">
                  Transform your business with our comprehensive POS solution. 
                  Streamline operations, boost sales, and gain valuable insights.
                </p>
              </div>

              {/* Feature Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="group bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10 hover:bg-white/10 hover:border-white/20 transition-all duration-500 hover:scale-105">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl shadow-lg group-hover:scale-110 transition-transform duration-300" style={{
                      background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                    }}>
                      💰
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold mb-2 text-white">Sales Management</h3>
                      <p className="text-gray-300 text-sm leading-relaxed">
                        Complete sales tracking with real-time analytics and reporting
                      </p>
                    </div>
                  </div>
                </div>

                <div className="group bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10 hover:bg-white/10 hover:border-white/20 transition-all duration-500 hover:scale-105">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl shadow-lg group-hover:scale-110 transition-transform duration-300" style={{
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                    }}>
                      📊
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold mb-2 text-white">Inventory Control</h3>
                      <p className="text-gray-300 text-sm leading-relaxed">
                        Real-time stock management with automated alerts
                      </p>
                    </div>
                  </div>
                </div>

                <div className="group bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10 hover:bg-white/10 hover:border-white/20 transition-all duration-500 hover:scale-105">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl shadow-lg group-hover:scale-110 transition-transform duration-300" style={{
                      background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)'
                    }}>
                      👥
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold mb-2 text-white">Customer Analytics</h3>
                      <p className="text-gray-300 text-sm leading-relaxed">
                        Deep customer insights with behavioral analysis
                      </p>
                    </div>
                  </div>
                </div>

                <div className="group bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10 hover:bg-white/10 hover:border-white/20 transition-all duration-500 hover:scale-105">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl shadow-lg group-hover:scale-110 transition-transform duration-300" style={{
                      background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'
                    }}>
                      📈
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold mb-2 text-white">Business Intelligence</h3>
                      <p className="text-gray-300 text-sm leading-relaxed">
                        Advanced analytics dashboard with predictive insights
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Right Side - Enhanced Login Form */}
            <div className="flex justify-center">
              <div className="w-full max-w-md">
                <div className="bg-white/10 backdrop-blur-xl rounded-3xl shadow-2xl p-8 border border-white/20 relative overflow-hidden">
                  {/* Form Background Glow */}
                  <div className="absolute inset-0 rounded-3xl" style={{
                    background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.2) 0%, rgba(118, 75, 162, 0.2) 100%)'
                  }}></div>
                  
                  {/* Decorative Elements */}
                  <div className="absolute top-0 right-0 w-32 h-32 rounded-full blur-2xl" style={{
                    background: 'linear-gradient(135deg, rgba(255, 215, 0, 0.2) 0%, transparent 100%)'
                  }}></div>
                  <div className="absolute bottom-0 left-0 w-24 h-24 rounded-full blur-xl" style={{
                    background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.2) 0%, transparent 100%)'
                  }}></div>
                  
                  <div className="relative z-10">
                    <div className="text-center mb-8">
                      <div className="w-16 h-16 rounded-2xl mx-auto mb-4 flex items-center justify-center shadow-lg" style={{
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                      }}>
                        <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                      </div>
                      <h2 className="text-3xl font-bold text-white mb-2">
                        Welcome Back, <span className="bg-gradient-to-r from-yellow-400 to-yellow-200 bg-clip-text text-transparent">{getCompanyName()}</span>
                      </h2>
                      <p className="text-gray-300">Sign in to your account to continue</p>
                    </div>
                    
                    <form onSubmit={handleLogin} className="space-y-6">
                      <div className="space-y-2">
                        <label htmlFor="loginId" className="block text-sm font-semibold text-gray-200">
                          Username or Email
                        </label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                            <svg className={`w-5 h-5 transition-colors duration-200 ${focusedField === 'loginId' ? 'text-yellow-400' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                            </svg>
                          </div>
                          <input 
                            type="text" 
                            id="loginId" 
                            name="loginId" 
                            value={loginData.loginId}
                            onChange={handleInputChange}
                            onFocus={() => setFocusedField('loginId')}
                            onBlur={() => setFocusedField(null)}
                            className="w-full pl-12 pr-4 py-4 bg-white/10 border border-white/20 rounded-xl focus:ring-2 focus:ring-yellow-400 focus:border-transparent transition-all duration-300 text-white placeholder-gray-400 backdrop-blur-sm hover:bg-white/15"
                            placeholder="Enter your username or email"
                            autoComplete="username"
                            disabled={formState.isDisabled}
                            required 
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <label htmlFor="password" className="block text-sm font-semibold text-gray-200">
                          Password
                        </label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                            <svg className={`w-5 h-5 transition-colors duration-200 ${focusedField === 'password' ? 'text-yellow-400' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                            </svg>
                          </div>
                          <input 
                            type={showPassword ? "text" : "password"}
                            id="password" 
                            name="password" 
                            value={loginData.password}
                            onChange={handleInputChange}
                            onFocus={() => setFocusedField('password')}
                            onBlur={() => setFocusedField(null)}
                            className="w-full pl-12 pr-12 py-4 bg-white/10 border border-white/20 rounded-xl focus:ring-2 focus:ring-yellow-400 focus:border-transparent transition-all duration-300 text-white placeholder-gray-400 backdrop-blur-sm hover:bg-white/15"
                            placeholder="Enter your password"
                            autoComplete="current-password"
                            disabled={formState.isDisabled}
                            required 
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-yellow-400 transition-colors duration-200"
                          >
                            {showPassword ? (
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                              </svg>
                            ) : (
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                              </svg>
                            )}
                          </button>
                        </div>
                      </div>

                      <div className="flex items-center justify-between">
                        <label className="flex items-center group cursor-pointer" onClick={() => setRememberMe(!rememberMe)}>
                          <div className="relative">
                            <input 
                              type="checkbox" 
                              checked={rememberMe}
                              onChange={(e) => setRememberMe(e.target.checked)}
                              className="sr-only"
                            />
                            <div className={`w-5 h-5 border-2 rounded transition-all duration-200 flex items-center justify-center ${
                              rememberMe 
                                ? 'border-yellow-400 bg-yellow-400' 
                                : 'border-gray-400 bg-white/10 group-hover:border-yellow-400'
                            }`}>
                              <svg className={`w-3 h-3 text-white transition-opacity duration-200 ${rememberMe ? 'opacity-100' : 'opacity-0'}`} fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                            </div>
                          </div>
                          <span className="ml-3 text-sm text-gray-300 group-hover:text-white transition-colors duration-200">Remember me</span>
                        </label>
                        <a 
                          href="#forgot" 
                          className="text-sm transition-colors duration-200 font-medium" 
                          style={{color: '#ffd700'}}
                        >
                          Forgot password?
                        </a>
                      </div>

                      <button 
                        type="submit" 
                        disabled={formState.isDisabled}
                        className="w-full text-white py-4 px-6 rounded-xl font-semibold focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:ring-offset-2 focus:ring-offset-transparent transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed shadow-xl hover:shadow-2xl transform hover:scale-105 relative overflow-hidden group"
                        style={{
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                        }}
                      >
                        <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                        <div className="relative flex items-center justify-center space-x-3">
                          {formState.showSpinner ? (
                            <>
                              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                              <span>{formState.buttonText}</span>
                            </>
                          ) : (
                            <>
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
                              </svg>
                              <span>{formState.buttonText}</span>
                            </>
                          )}
                        </div>
                      </button>

                      <div className="text-center pt-6 border-t border-white/10">
                        <div className="flex items-center justify-center space-x-2 mb-3">
                          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{
                            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                          }}>
                            <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                            </svg>
                          </div>
                          <span className="text-xs text-gray-400 font-medium">Enterprise-grade security</span>
                        </div>
                        <p className="text-sm text-gray-300 font-medium">
                          Protected by SSL encryption and secure authentication
                        </p>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

Homepage.displayName = 'Homepage';

export default Homepage;